<script setup lang="ts">
import { computed } from 'vue';

import { AuthenticationRequestError, authRequest, type AuthenticationResult } from '../api.ts';
import { useAuthenticationAction } from '../authenticationAction.ts';
import type { AuthenticationStart } from '../types.ts';

type LinkStart = Extract<AuthenticationStart, { screen: 'authentication-link' }>;

const props = defineProps<{
    start: LinkStart;
    completing: boolean;
    resolve: (result: AuthenticationResult) => Promise<void>;
}>();
const emit = defineEmits<{
    expired: [title: string, detail: string];
}>();
const { busy, run } = useAuthenticationAction();
const loading = computed(() => busy.value || props.completing);

function consumeLink() {
    void run(
        async () => {
            await props.resolve(
                await authRequest<AuthenticationResult>('links/' + props.start.token),
            );
        },
        (reason) => {
            if (reason instanceof AuthenticationRequestError && reason.response.status === 422) {
                emit(
                    'expired',
                    'Link Invalid or Expired',
                    'Request a new secure link to sign in or create your account.',
                );
                return true;
            }
        },
    );
}

if (props.start.consumeImmediately) {
    consumeLink();
}
</script>

<template>
    <div v-if="start.consumeImmediately && loading" class="placeholder grow justify-center m-0 p-0">
        <div class="spinner" />
        <p>Signing you in…</p>
    </div>

    <div v-else class="stack gap-xl text-center">
        <div class="stack gap-md">
            <h2>Continue to eMedici</h2>
            <p class="color-muted text-sm">Continue securely to complete this request.</p>
        </div>

        <button
            class="btn bg-primary btn--lg full-width"
            :class="{ 'is-loading': loading }"
            :disabled="loading"
            @click="consumeLink"
        >
            Continue
        </button>
    </div>
</template>
